import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { WorkOrder, SearchCriteria, CountingDocument, SearchCriteriaItem } from '@raptor-fe/core';
import { ServiceUtils } from '../service-utils/service-utils';
import { LoadingHeaders } from '../web-loading-service/web-loading-headers';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class WorkOrderService {
    private readonly http = inject(HttpClient);
    private readonly serviceUtils = inject(ServiceUtils);
    private httpHeadersWithNoLoading = LoadingHeaders.httpHeadersWithNoLoading();

    applyStatusCriteria(status: string, criteriaItems: SearchCriteriaItem[]): void {
        if (status === 'active' || status === 'completed') {
            criteriaItems.push({
                condition: 'and',
                name: 'completedDate',
                operator: status === 'active' ? 'isNull' : 'isNotNull',
                value: null,
            });
        } else if (status === 'pendingApproval') {
            criteriaItems.push({
                condition: 'and',
                name: 'countingDocument.submittedDate',
                operator: '<>',
                value: null,
            });
            criteriaItems.push({
                condition: 'and',
                name: 'countingDocument.approvalDate',
                operator: '=',
                value: null,
            });
        } else if (status === 'all') {
            criteriaItems.push({
                condition: 'and',
                name: 'number',
                operator: '<>',
                value: null,
            });
        }
    }

    transferFromQuote(tenantId: string, branchId: string, workOrderId: string, quoteId: string, lineIds: string[]): Observable<WorkOrder> {
        return this.http.post<WorkOrder>(
            this.serviceUtils.getBaseUrl('workOrder', tenantId, branchId) + `/${workOrderId}/transfer-from-quote/${quoteId}`,
            { lineIds },
        );
    }

    search(tenantId: string, branchId: string, criteria: SearchCriteria, showLoading = true): Observable<WorkOrder[]> {
        return this.http.post<WorkOrder[]>(this.serviceUtils.getBaseUrl('workOrder', tenantId, branchId) + `/search`, criteria, {
            headers: showLoading ? undefined : this.httpHeadersWithNoLoading,
        });
    }

    searchByKeyword(
        tenantId: string,
        branchId: string,
        keyword: string,
        status: 'active' | 'completed' | 'pendingApproval' | 'all',
        criteria: SearchCriteria,
        showLoading = true,
    ): Observable<WorkOrder[]> {
        this.applyStatusCriteria(status, criteria.items);

        let url = this.serviceUtils.getBaseUrl('workOrder', tenantId, branchId) + `/search/keyword`;
        if (keyword && keyword.length > 0) url += `/${encodeURIComponent(keyword)}`;

        return this.http.post<WorkOrder[]>(url, criteria, {
            headers: showLoading ? undefined : this.httpHeadersWithNoLoading,
        });
    }

    get(tenantId: string, branchId: string, id: string): Observable<WorkOrder> {
        return this.http.get<WorkOrder>(this.serviceUtils.getBaseUrl('workOrder', tenantId, branchId) + `/${id}`);
    }

    /**
     * Reads the work order for the technician (GET .../workOrder/{id}/field), its sections and items as Raptor
     * stores them, kept current by the work order syncs and by each remark sent. Gated on the Work Order Field
     * Remarks flag; calling it again is the refresh. Amounts arrive only when the branch lets technicians see prices.
     */
    getForTechnician(tenantId: string, branchId: string, id: string, showLoading = true): Observable<WorkOrder> {
        return this.http.get<WorkOrder>(this.serviceUtils.getBaseUrl('workOrder', tenantId, branchId) + `/${encodeURIComponent(id)}/field`, {
            headers: showLoading ? undefined : this.httpHeadersWithNoLoading,
        });
    }

    /**
     * Writes a technician's remark to a section of the federated work order (POST .../workOrder/{id}/remarks) and
     * returns the work order as Raptor stores it afterwards. Gated on the Work Order Field Remarks flag. The
     * author and time are added on the server. `sectionId` is empty for a work order with no section of its own
     * yet. Resending with `retry` set is safe: the server does not write again a remark the earlier send already
     * wrote. `copyToPlanner` also appends the remark to the booking's planner notes when there is one.
     */
    addRemark(
        tenantId: string,
        branchId: string,
        id: string,
        remark: { sectionId: string; description: string; packageId?: string | null },
        options: { copyToPlanner: boolean; retry: boolean },
    ): Observable<WorkOrder> {
        const params = new HttpParams().set('copyToPlanner', String(options.copyToPlanner)).set('retry', String(options.retry));
        return this.http.post<WorkOrder>(this.serviceUtils.getBaseUrl('workOrder', tenantId, branchId) + `/${encodeURIComponent(id)}/remarks`, remark, {
            headers: this.httpHeadersWithNoLoading,
            params,
        });
    }

    /**
     * Creates a Work Order from a minimal payload (CustomerId + VehicleId) via
     * POST /api/v1/tenant/{tenantId}/branch/{branchId}/workOrder/quick-create. The full
     * Create endpoint requires Date/Number/StatusId/TypeId for model validation; this
     * endpoint accepts a stripped DTO and lets the service layer + DB sequence fill the
     * rest. Used by the Inspection Create modal "+" inline-create button.
     */
    quickCreate(tenantId: string, branchId: string, customerId: string, vehicleId: string): Observable<WorkOrder> {
        return this.http.post<WorkOrder>(this.serviceUtils.getBaseUrl('workOrder', tenantId, branchId) + `/quick-create`, { customerId, vehicleId });
    }

    complete(tenantId: string, branchId: string, workOrder: WorkOrder): Observable<WorkOrder> {
        return this.http.put<WorkOrder>(this.serviceUtils.getBaseUrl('workOrder', tenantId, branchId) + `/complete`, workOrder);
    }

    updatePartQuantitiesAndComplete(tenantId: string, branchId: string, id: string): Observable<WorkOrder> {
        return this.http.put<WorkOrder>(
            this.serviceUtils.getBaseUrl('workOrder', tenantId, branchId) + `/update-part-quantities-and-complete/${id}`,
            {},
        );
    }

    assignCountingDocument(tenantId: string, branchId: string, workOrderId: string, countingDocument: CountingDocument): Observable<WorkOrder> {
        return this.http.put<WorkOrder>(
            this.serviceUtils.getBaseUrl('workOrder', tenantId, branchId) + `/assign-counting-document/${workOrderId}`,
            countingDocument,
        );
    }
}
